// src/app/modules/user/modules/accounts/pages/DoctorDeviceListingPage.tsx
import React, {useEffect, useMemo, useState} from 'react'
import {UserDataModel} from '../../../../entities/entities'
import DataTableList from '../../components/DataTableList'
import {RoutesMetaForDoctors} from '../../modules/accounts/components/CRUDWrapper/routesMeta'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {
  DOCTOR_DEVICE_DETAILS_HEADERS,
  DOCTOR_DEVICE_HEADERS,
} from '../../../../constants/RoleConstants'
import {DeviceTypeModel} from '../../../../entities/device.entity'
import {toast} from 'react-toastify'
import DeviceDetailsModal from '../../components/modals/DeviceDetailsModal'
import {useReduxAuth} from '../../../auth'
import {currentMonthYear, filterHeadersByUserRole} from '../../helper/helperFunction'
import MetaTags from '../../../../meta/MetaTags'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import {Modal, Button} from 'react-bootstrap'
import GenericDropdown, {GenericItem} from '../../components/common/GenericDropdown'
import PrimaryButton from '../../components/common/buttons/PrimaryButton'
import UnassignDeviceConfirmationModal from '../../components/modals/UnassignDeviceConfirmationModal'

// Stable noop function to prevent useEffect dependency changes
const noop = () => {}

interface DeviceListingProps {
  userData: UserDataModel
}

const DoctorDeviceListingPage = ({userData}: DeviceListingProps) => {
  const notifyError = (msg: string) => toast.error(msg)
  const notifyWarning = (msg: string) => toast.warning(msg)
  const notifySuccess = (msg: string) => toast.success(msg)

  const {currentUser} = useReduxAuth()
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  const setMaSelectedDoctorId = currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.setSelectedDoctorId : noop
  
  // For MA, use selected doctor ID; for others, use userData.id
  const doctorIdForRequest = currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId 
    ? maSelectedDoctorId 
    : userData.id
  
  // State for doctor dropdown (for MA)
  const [maDoctors, setMaDoctors] = useState<Array<{id: number; full_name: string}>>([])

  const [devices, setDevices] = useState<any[]>([])
  const [deviceSpecificData, setDeviceSpecificData] = useState<any[]>([])
  const [deviceTypes, setDeviceTypes] = useState<DeviceTypeModel[]>([])
  const [isDeviceInfoModalOpen, setIsDeviceInfoModalOpen] = useState(false)
  const [selectedDeviceForView, setSelectedDeviceForView] = useState<any>(null)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // ---- assign-patient state ----
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
  const [selectedDevice, setSelectedDevice] = useState<any>(null)
  const [patients, setPatients] = useState<GenericItem[] & {uuid: string}[]>([])
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null)
  const [assigning, setAssigning] = useState(false)

  // ---- unassign-device state ----
  const [unassigningDeviceId, setUnassigningDeviceId] = useState<number | null>(null)
  const [isUnassignModalOpen, setIsUnassignModalOpen] = useState(false)
  const [deviceToUnassign, setDeviceToUnassign] = useState<any>(null)

  // Fetch all doctors for MA (MA can access all doctors via dropdown)
  useEffect(() => {
    if (currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT) {
      axios
        .get(API_ENDPOINTS.GET_ALL_DOCTORS())
        .then((resp) => {
          const fetchedDoctors = resp.data.data?.doctors || resp.data.data || []
          setMaDoctors(fetchedDoctors)
          if (fetchedDoctors.length > 0 && !maSelectedDoctorId) {
            setMaSelectedDoctorId(fetchedDoctors[0].id)
          }
        })
        .catch((err) => {
          console.error('Error fetching doctors for MA:', err)
          toast.error('Failed to load doctors')
          setMaDoctors([])
        })
    }
  }, [currentUser.role_id, maSelectedDoctorId, setMaSelectedDoctorId])

  // Fetch all devices
  useEffect(() => {
    // Skip if MA and no doctor selected
    if (currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT && !maSelectedDoctorId) {
      setDevices([])
      setIsLoading(false)
      return
    }

    const fetchDevices = async () => {
      setIsLoading(true)
      try {
        // For MA, use selected doctor ID directly in route (same as doctors do)
        // For doctors, use their own ID
        const doctorId = currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId 
          ? maSelectedDoctorId 
          : userData.id
        
        const resp = await axios.get(API_ENDPOINTS.GET_ALL_DOCTOR_DEVICES(doctorId))
        
        // Handle different response structures
        const devicesData = resp.data?.data?.devices || resp.data?.devices || resp.data?.data || []
        
        setDevices(Array.isArray(devicesData) ? devicesData : [])
      } catch (err: any) {
        notifyError(err?.response?.data?.message || err.message || 'Failed to load devices')
        setDevices([])
      }
      setIsLoading(false)
    }
    fetchDevices()
  }, [userData.id, maSelectedDoctorId, currentUser.role_id])

  // Fetch device types (unchanged)
  useEffect(() => {
    const fetchDeviceTypes = async () => {
      try {
        const resp = await axios.get(API_ENDPOINTS.GET_ALL_DEVICE_TYPES())
        const dd = resp.data?.data?.devices || []
        setDeviceTypes(dd.map((d: any) => ({id: d.id, name: d.name})))
        setDeviceSpecificData(
          dd.map((d: any) => ({
            id: d.Devices?.[0]?.id,
            name: d.Devices?.[0]?.name,
            stripeProductId: d.Devices?.[0]?.stripe_product_id,
            stripeProductPriceId: d.Devices?.[0]?.stripe_product_price_id,
            price: d.Devices?.[0]?.price,
          }))
        )
      } catch (err: any) {
        notifyError(err.message || err)
      }
    }
    fetchDeviceTypes()
  }, [])

  // Handle table actions
  const handleActionPerformed = async (actionType: any, row: any) => {
    switch (actionType.name) {
      case 'VIEW':
        setSelectedDeviceForView(row)
        setIsDeviceInfoModalOpen(true)
        break

      case 'ASSIGN_DEVICE':
        setSelectedDevice(row)
        setIsAssignModalOpen(true)
        try {
          // For MA, add doctor_id to query params; for others, use uuid
          const qsParams = new URLSearchParams({
            month_year: currentMonthYear,
            status: 'active',
          })
          
          if (currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
            qsParams.append('doctor_id', maSelectedDoctorId.toString())
          } else {
            qsParams.append('uuid', userData.uuid)
          }
          
          const resp = await axios.get(
            API_ENDPOINTS.GET_MY_PATIENTS(qsParams.toString())
          )
          // Each patient has .id and .uuid
          const allPatients = resp.data?.data?.patients || []
          // Only show patients who don't have an active device allocation
          const eligible = allPatients.filter(
            (p: any) =>
              Array.isArray(p.DeviceAllocations) &&
              p.DeviceAllocations.every((alloc: any) => alloc.devices_de_allocation_date !== null)
          )
          
          const mappedPatients = eligible.map((p: any) => ({
            id: p.id,
            uuid: p.uuid,
            name: p.full_name,
          }))
          setPatients(mappedPatients)
        } catch (err: any) {
          notifyError(err.message || err)
        }
        break

      case 'UNASSIGN_DEVICE':
        // Check if device is actually assigned to a patient
        const deviceAllocation = row?.Device?.AllocatedDevice?.[0]
        if (!deviceAllocation) {
          return notifyWarning('This device is not currently assigned to any patient.')
        }
        // Show confirmation modal
        setDeviceToUnassign(row)
        setIsUnassignModalOpen(true)
        break

      default:
        console.log('')
    }
  }

  // When user clicks "Assign" in the modal:
  const assignDeviceToPatient = async () => {
    if (!selectedDevice || selectedPatientId === null) {
      return notifyError('Please select a patient first.')
    }
    setAssigning(true)
    setIsLoading(true)
    try {
      // find the patient object so we can get its uuid
      const patient = patients.find((p) => p.id === selectedPatientId)
      if (!patient) {
        throw new Error('Selected patient not found')
      }
      const doctorAllocatedId = selectedDevice?.id
      if (!doctorAllocatedId) {
        return notifyError('No allocation record found for this device.')
      }

      // call your assign endpoint with the patient.uuid
      await axios.post(API_ENDPOINTS.ASSIGN_PATIENT_DEVICE(patient.uuid!), {
        device_id: selectedDevice.device_id,
        doctor_allocated_device_id: doctorAllocatedId,
      })
      notifySuccess('Device assigned successfully.')
      setIsAssignModalOpen(false)

      // refresh device list
      const doctorId = currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId 
        ? maSelectedDoctorId 
        : userData.id
      const resp = await axios.get(API_ENDPOINTS.GET_ALL_DOCTOR_DEVICES(doctorId))
      setDevices(resp.data?.data?.devices || [])
    } catch (err: any) {
      notifyWarning(err?.response?.data?.message || err)
    } finally {
      setAssigning(false)
      setIsLoading(false)
    }
  }

  // Handle unassigning device from patient (called after user confirms)
  const handleUnassignDevice = async () => {
    if (!deviceToUnassign) return

    const deviceAllocation = deviceToUnassign?.Device?.AllocatedDevice?.[0]
    const deviceAllocationId = deviceAllocation?.id
    if (!deviceAllocationId) {
      return notifyError('Device allocation ID not found.')
    }

    setUnassigningDeviceId(deviceToUnassign.id)
    setIsLoading(true)

    try {
      await axios.put(
        API_ENDPOINTS.UNASSIGN_PATIENT_DEVICE(deviceAllocationId),
        {}
      )
      notifySuccess('Device unassigned successfully.')

      // refresh device list
      const doctorId = currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? maSelectedDoctorId
        : userData.id
      const resp = await axios.get(API_ENDPOINTS.GET_ALL_DOCTOR_DEVICES(doctorId))
      setDevices(resp.data?.data?.devices ?? [])
    } catch (err: any) {
      notifyError(err?.response?.data?.message ?? 'Failed to unassign device')
    } finally {
      setUnassigningDeviceId(null)
      setIsLoading(false)
      setIsUnassignModalOpen(false)
      setDeviceToUnassign(null)
    }
  }

  const filteredHeaders = filterHeadersByUserRole(DOCTOR_DEVICE_HEADERS, currentUser)
  const filteredHeadersForDetails = filterHeadersByUserRole(
    DOCTOR_DEVICE_DETAILS_HEADERS,
    currentUser
  )
  // search filters from datatables
  const [searchText, setSearchText] = useState('')
  const filteredData = useMemo(() => {
    const filtered = devices.filter((device: any) => {
      // Add null checks to prevent errors
      if (!device) {
        return false
      }
      
      if (!device.Device) {
        return false
      }

      // Search text filter
      const searchFilter =
        searchText === '' ||
        device.Device?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        device.Device?.imei?.toLowerCase().includes(searchText.toLowerCase()) ||
        device.Device?.AllocatedDevice?.[0]?.Patient?.full_name
          ?.toLowerCase()
          .includes(searchText.toLowerCase())

      return searchFilter
    })
    
    return filtered
  }, [devices, searchText])
  const handleFilterChange = (key: string, value: string) => {
    switch (key) {
      case 'search':
        setSearchText(value)
        break
      default:
        break
    }
  }
  const handleClearAllFilters = () => {
    setSearchText('')
  }

  const customFilters = [
    {
      name: 'Search',
      key: 'search',
      type: 'search' as const,
      placeholder: 'Search by name, imei, or patient name',
      value: searchText,
    },
  ]

  return (
    <>
      <MetaTags title='Device List' />

      {/* Device Details Modal */}
      <DeviceDetailsModal
        show={isDeviceInfoModalOpen}
        onHide={() => {
          setIsDeviceInfoModalOpen(false)
          setSelectedDeviceForView(null)
        }}
        device={selectedDeviceForView}
      />

      {/* Assign Device Modal */}
      <Modal show={isAssignModalOpen} onHide={() => setIsAssignModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedDevice?.Device?.name || 'Unknown Device'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-2'>Select an active patient without an assigned device:</p>
          {patients.length > 0 ? (
            <GenericDropdown
              items={patients}
              selectedId={selectedPatientId}
              onSelect={setSelectedPatientId}
              placeholder='Choose a patient'
            />
          ) : (
            <div className='alert alert-info'>
              No eligible active patients found. Only active patients without an assigned device can
              be selected.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            className='text-white'
            onClick={() => setIsAssignModalOpen(false)}
            disabled={assigning}
          >
            Cancel
          </Button>
          <PrimaryButton
            color='primary'
            disabled={selectedPatientId === null || assigning || patients.length === 0}
            onClick={() => {
              assignDeviceToPatient()
            }}
          >
            {assigning ? 'Assigning…' : 'Assign'}
          </PrimaryButton>
        </Modal.Footer>
      </Modal>

      {/* Unassign Device Confirmation Modal */}
      <UnassignDeviceConfirmationModal
        isOpen={isUnassignModalOpen}
        title="Unassign Device"
        description={`Are you sure you want to unassign "${deviceToUnassign?.Device?.name || 'this device'}" from ${deviceToUnassign?.Device?.AllocatedDevice?.[0]?.Patient?.full_name || 'the patient'}?`}
        confirmText="Unassign"
        cancelText="Cancel"
        onConfirm={handleUnassignDevice}
        onCancel={() => {
          setIsUnassignModalOpen(false)
          setDeviceToUnassign(null)
        }}
      />

      {/* Doctor Dropdown for MA */}
      {currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT && (
        <div className='d-flex justify-content-end mb-3'>
          <div className='w-25'>
            <label className='form-label'>Select Doctor</label>
            <select
              className='form-select'
              value={maSelectedDoctorId ?? ''}
              onChange={(e) => {
                const val = e.target.value
                setMaSelectedDoctorId(val ? Number(val) : undefined)
              }}
              disabled={maDoctors.length === 0}
            >
            {maDoctors.length === 0 ? (
              <option value=''>-- No doctors available --</option>
            ) : (
              <>
                <option value=''>-- Select Doctor --</option>
                {maDoctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.full_name}
                  </option>
                  ))}
              </>
            )}
            </select>
          </div>
        </div>
      )}

      {/* Main Data Table */}
      <DataTableList
        isLoading={isLoading}
        title='Device List'
        columns={filteredHeadersForDetails}
        data={filteredData}
        actions={RoutesMetaForDoctors.devices.actions}
        onAction={handleActionPerformed}
        showAddButton={false}
        showOnlySearch={true}
        showCustomFilters={true}
        customFilters={customFilters}
        onCustomFilterChange={handleFilterChange}
        onClearAllCustomFilters={handleClearAllFilters}
        customInputWidth="300px"
      />
    </>
  )
}

export default DoctorDeviceListingPage
